
	import LoadingComponent from "custom/LoadingComponent"
	import Component from "vue-class-component"
	import { Watch } from "vue-property-decorator"

	import PWizard from "@portal-components/ui/wizard/PWizard.vue"
	import PWizardSlide from "@portal-components/ui/wizard/PWizardSlide.vue"
	import PTextInput from "@portal-components/ui/input/PTextInput.vue"
	import PButton from "@portal-components/ui/input/PButton.vue"

	import IcsPasswordInput from "custom/ui/IcsPasswordInput.vue"
	import IcsUsernameInput from "custom/ui/IcsUsernameInput.vue"
	import QrCode from "custom/login/QrCode.vue"
	import ResponseCode from "custom/login/ResponseCode.vue"

	import { StoreAction } from "@lib/types/vuex"
	import { JwtToken, UsernamePassword } from "@lib/types/security"
	import eventBus from "@lib/vue/eventBus"
	import { IDENTIFICATION_FAILED, ACCESS_DENIED } from "@lib/vue/events"

	import { authenticationModule, userModule } from "store/modules"
	import { eRecognitionConfig } from "api/settings"
	import { LoginActionPayload } from "store/authentication/actions"
	import { AuthenticationMethod, sessionFactory } from "utils/session-factory"
	import twoFactorAuthenticationClient from "api/clients/2fa-client"
	import { LoginFailedReason, loginFailedReasonConversion } from "utils/loginFailedReasonConversion"
	import localStorageClient from "api/clients/local-storage-client"

	@Component({
		components: {
			PTextInput,
			PButton,
			IcsPasswordInput,
			IcsUsernameInput,
			PWizard,
			PWizardSlide,
			QrCode,
			ResponseCode
		}
	})
	export default class LoginForm extends LoadingComponent {
		@authenticationModule.Action login!: StoreAction<LoginActionPayload>
		@userModule.Getter hasError!: boolean
		@userModule.Action resetErrorState!: StoreAction<void>
		@authenticationModule.Getter loggedIn!: boolean

		readonly baseTranslationKey = "login"
		readonly LoginFailedReason = LoginFailedReason
		readonly REDIRECT_TIMEOUT = 1500

		eRecognitionLoginUrl = ""
		loginFailed = false
		loginFailedReason = LoginFailedReason.UNKNOWN
		is2FaEnabled = false
		is2FaActivatedForUser = false
		currentSlide = 1

		credentials: UsernamePassword = {
			username: "",
			password: ""
		}

		async beforeMount(): Promise<void> {
			this.eRecognitionLoginUrl = eRecognitionConfig.loginUrl // Bind it like this otherwise the binding does not work...
			this.is2FaEnabled = await twoFactorAuthenticationClient.isEnabled()
		}

		@Watch("credentials", { deep: true })
		async stateChanged(): Promise<void> {
			this.loginFailed = false

			if (this.hasError) {
				await this.resetErrorState()
			}
		}

		clickLink(): void {
			window.location.href = this.eRecognitionLoginUrl
		}

		async authorize(): Promise<void> {
			eventBus.once([IDENTIFICATION_FAILED, ACCESS_DENIED], (reason: string) => {
				this.loginFailedReason = loginFailedReasonConversion(reason) ?? LoginFailedReason.UNKNOWN
				this.loginFailed = true
			})
			if (this.is2FaEnabled) {
				localStorageClient.setItem("2faAuthentication",  "1")
			}
			await this.manageLoadingState(this.login({
				authenticationMethod: AuthenticationMethod.CREDENTIALS,
				credentials: this.credentials
			}))

			if (this.is2FaEnabled) {
				this.is2FaActivatedForUser = await twoFactorAuthenticationClient.is2FaActivated()
				setTimeout(() => this.nextSlide(), this.REDIRECT_TIMEOUT)
			} else {
				await sessionFactory.session.verify()
			}
		}

		async handle2FaResponse(token: JwtToken): Promise<void> {
			localStorageClient.setItem("2faAuthentication",  "0")
			await sessionFactory.session.authorizeWithToken(token)
		}

		private nextSlide(): void {
			this.currentSlide++
		}
	}
